import { useEffect, useState } from "react";
import { api } from "../lib/api";
import JarvisOrb from "../components/JarvisOrb";
import VoiceController from "../components/VoiceController";
import { Users, Hotel, CheckCircle2, Clock, AlertTriangle, Download, Mic, Calendar } from "lucide-react";
import { useAuth } from "../lib/auth";

const STATE_LABELS = {
  idle: "TÜM SÜREÇLER KONTROL ALTINDA",
  listening: "DİNLİYORUM... SİZİ DUYUYORUM",
  thinking: "VERİ ANALİZ EDİLİYOR...",
  speaking: "JARVIS YANITLIYOR",
};

const SAMPLE_QUERIES = [
  "Bugün kaç personel geldi?",
  "Hangi şefler puantaj girmedi?",
  "Arsi Blue mutfak durumu nedir?",
  "Günün özeti nedir?",
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [completion, setCompletion] = useState([]);
  const [audit, setAudit] = useState([]);
  const [orbState, setOrbState] = useState("idle");
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [textInput, setTextInput] = useState("");

  const loadAll = async () => {
    try {
      const [s, h, c, a] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/hotels"),
        api.get("/puantaj/completion-status"),
        api.get("/dashboard/audit-log"),
      ]);
      setStats(s.data);
      setHotels(h.data);
      setCompletion(c.data);
      setAudit(a.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 15000);
    return () => clearInterval(t);
  }, []);

  const sendTextQuery = async (q) => {
    const text = q || textInput;
    if (!text) return;
    setTextInput("");
    setTranscript(text);
    setOrbState("thinking");
    try {
      const chatRes = await api.post("/jarvis/chat", { message: text });
      setReply(chatRes.data.reply);
      // TTS
      setOrbState("speaking");
      const ttsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("jarvis_token")}` },
        body: JSON.stringify({ text: chatRes.data.reply }),
      });
      if (ttsRes.ok) {
        const blob = await ttsRes.blob();
        const audio = new Audio(URL.createObjectURL(blob));
        audio.onended = () => setOrbState("idle");
        audio.play();
      } else { setOrbState("idle"); }
    } catch (e) {
      setReply("Yanıt oluşturulamadı.");
      setOrbState("idle");
    }
  };

  const downloadExcel = async () => {
    const token = localStorage.getItem("jarvis_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export-today`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `arsi_puantaj_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!stats) return <div className="text-center py-20 font-mono text-cyan-400">SİSTEM BAŞLATILIYOR...</div>;

  const sc = stats.today_counts;

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Top metric strip */}
      <div data-testid="metric-strip" className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <MetricCard label="Toplam Personel" value={stats.total_personnel} icon={Users} color="cyan" testid="metric-total" />
        <MetricCard label="Bugün Çalışan" value={sc.CALISIYOR} icon={CheckCircle2} color="emerald" testid="metric-active" />
        <MetricCard label="İzinli" value={sc.IZINLI + sc.HAFTA_TATILI + sc.UCRETSIZ_IZIN} icon={Clock} color="sky" testid="metric-leave" />
        <MetricCard label="Raporlu" value={sc.RAPORLU} icon={AlertTriangle} color="amber" testid="metric-report" />
        <MetricCard label="Gelmeyen" value={sc.GELMEDI} icon={AlertTriangle} color="rose" testid="metric-absent" />
      </div>

      {/* 3-column command grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* LEFT: Hotels completion */}
        <div className="lg:col-span-3 space-y-4">
          <div className="glass-card hud-corners p-5">
            <h3 className="font-display text-sm font-bold text-cyan-300 tracking-widest uppercase mb-4 text-glow-sm">Puantaj Durumu</h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {completion.map((c, idx) => (
                <div key={idx} data-testid={`completion-${c.hotel_code}-${c.department_code}`}
                     className={`flex items-center justify-between p-2 rounded text-xs border ${
                       c.submitted ? "bg-emerald-500/10 border-emerald-500/30" : "bg-rose-500/10 border-rose-500/30"
                     }`}>
                  <div>
                    <div className="font-mono text-slate-200">{c.hotel_name}</div>
                    <div className="text-[10px] text-slate-400">{c.department_name}</div>
                  </div>
                  <div className={`text-[10px] font-mono font-bold ${c.submitted ? "text-emerald-400" : "text-rose-400"}`}>
                    {c.submitted ? "✓" : "⏳"}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xs font-bold text-cyan-300 tracking-widest uppercase">Tamamlanma</h3>
              <span className="font-mono text-2xl font-black text-cyan-300 text-glow">%{stats.completion_percent}</span>
            </div>
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-sky-400 transition-all"
                   style={{ width: `${stats.completion_percent}%`, boxShadow: "0 0 10px rgba(0,212,255,0.6)" }} />
            </div>
            <div className="mt-2 font-mono text-[10px] text-slate-400 text-right">
              {stats.completed_departments} / {stats.total_departments_expected} departman
            </div>
          </div>
        </div>

        {/* CENTER: Orb + voice */}
        <div className="lg:col-span-6 glass-card hud-corners scan-overlay p-6 flex flex-col items-center relative overflow-hidden">
          <div className="font-mono text-[10px] tracking-[0.3em] text-cyan-400/80 uppercase mb-2">
            {STATE_LABELS[orbState]}
          </div>
          <JarvisOrb state={orbState} size={340} />
          <div className="mt-4 w-full max-w-md">
            {transcript && (
              <div className="mb-3 p-3 rounded-lg bg-slate-900/60 border border-cyan-500/20">
                <div className="text-[10px] font-mono text-cyan-400/60 uppercase tracking-widest mb-1">Siz</div>
                <div className="text-sm text-slate-200">{transcript}</div>
              </div>
            )}
            {reply && (
              <div data-testid="jarvis-reply" className="mb-3 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/30">
                <div className="text-[10px] font-mono text-cyan-400 uppercase tracking-widest mb-1">JARVIS</div>
                <div className="text-sm text-slate-100">{reply}</div>
              </div>
            )}
            <div className="flex items-center gap-2 mb-3">
              <input
                data-testid="jarvis-text-input"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendTextQuery()}
                placeholder="JARVIS'e sorun..."
                className="flex-1 px-4 py-2 bg-slate-950/60 border border-cyan-500/30 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none"
              />
              <button
                data-testid="jarvis-send-btn"
                onClick={() => sendTextQuery()}
                className="btn-cyan px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest"
              >Gönder</button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4 justify-center">
              {SAMPLE_QUERIES.map((q, i) => (
                <button key={i} onClick={() => sendTextQuery(q)}
                        className="text-[11px] font-mono px-3 py-1 rounded-full border border-cyan-500/25 text-cyan-300/80 hover:border-cyan-400 hover:text-cyan-200 hover:bg-cyan-500/10 transition">
                  {q}
                </button>
              ))}
            </div>
            <div className="flex justify-center">
              <VoiceController
                orbState={orbState}
                onStateChange={setOrbState}
                onTranscript={setTranscript}
                onReply={setReply}
              />
            </div>
          </div>
        </div>

        {/* RIGHT: Quick actions + audit */}
        <div className="lg:col-span-3 space-y-4">
          <div className="glass-card hud-corners p-5">
            <h3 className="font-display text-sm font-bold text-cyan-300 tracking-widest uppercase mb-3 text-glow-sm">Hızlı Erişim</h3>
            <div className="space-y-2">
              <button data-testid="quick-excel" onClick={downloadExcel}
                      className="w-full flex items-center gap-3 p-3 rounded-lg btn-cyan text-sm font-mono uppercase tracking-wider">
                <Download className="w-4 h-4" /> Excel Rapor
              </button>
              <a href="/raporlar" className="w-full flex items-center gap-3 p-3 rounded-lg btn-cyan text-sm font-mono uppercase tracking-wider">
                <Calendar className="w-4 h-4" /> Geçmiş Raporlar
              </a>
              <a href="/personel" className="w-full flex items-center gap-3 p-3 rounded-lg btn-cyan text-sm font-mono uppercase tracking-wider">
                <Users className="w-4 h-4" /> Personel Listesi
              </a>
            </div>
          </div>
          <div className="glass-card p-5">
            <h3 className="font-display text-sm font-bold text-cyan-300 tracking-widest uppercase mb-3 text-glow-sm">Son İşlemler</h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
              {audit.length === 0 && <div className="text-xs text-slate-500 font-mono">Henüz işlem yok</div>}
              {audit.map((a, i) => (
                <div key={i} className="p-2 rounded bg-slate-900/40 border border-cyan-500/10 text-xs">
                  <div className="text-cyan-300 font-mono">{a.action}</div>
                  <div className="text-slate-400 text-[10px]">{a.actor}</div>
                  <div className="text-slate-500 text-[10px] font-mono">{new Date(a.timestamp).toLocaleString("tr-TR")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hotels grid */}
      <div className="mt-6">
        <h3 className="font-display text-sm font-bold text-cyan-300 tracking-widest uppercase mb-3 text-glow-sm">OTEL GRUBU</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {hotels.map((h) => (
            <div key={h.code} data-testid={`hotel-card-${h.code}`} className="glass-card glass-card-interactive overflow-hidden">
              <div className="h-24 w-full bg-cover bg-center relative" style={{ backgroundImage: `url(${h.image})` }}>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050b14]/95 to-transparent" />
              </div>
              <div className="p-3">
                <div className="font-display text-sm font-bold text-cyan-200 truncate">{h.name}</div>
                <div className="text-[10px] text-slate-400 font-mono mb-2">{h.location}</div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-slate-300">
                    <Users className="w-3 h-3 text-cyan-400" /> {h.personnel_count}
                  </div>
                  <div className="text-[10px] font-mono text-cyan-400/80 uppercase">Aktif</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color, testid }) {
  const colors = {
    cyan: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5",
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    sky: "text-sky-300 border-sky-500/30 bg-sky-500/5",
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    rose: "text-rose-300 border-rose-500/30 bg-rose-500/5",
  }[color];
  return (
    <div data-testid={testid} className={`glass-card p-4 border ${colors}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">{label}</span>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="font-mono text-3xl font-black text-glow tabular-nums">{value}</div>
    </div>
  );
}
